import React, { useEffect, useCallback, useState, useRef } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import Link from '@tiptap/extension-link';
import Image from '@tiptap/extension-image';
import { Color } from '@tiptap/extension-color';
import { TextStyle } from '@tiptap/extension-text-style';
import TextAlign from '@tiptap/extension-text-align';
import { uploadFile } from '../utils/fileUpload';

const ToolbarButton = ({ onClick, isActive, children, title }) => (
  <button
    type="button"
    onMouseDown={(e) => e.preventDefault()}
    onClick={onClick}
    title={title}
    className={`px-1.5 py-0.5 text-xs font-semibold rounded cursor-pointer transition-colors min-w-[36px] h-[36px] md:min-w-[28px] md:h-[28px] flex items-center justify-center ${
      isActive ? 'bg-white/30' : 'hover:bg-white/15'
    }`}
  >
    {children}
  </button>
);

const RichTextEditor = ({ content, onUpdate, textColor, placeholder }) => {
  const [uploading, setUploading] = useState(false);
  const imageInputRef = useRef(null);

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      alert('Image must be under 5MB');
      return;
    }
    setUploading(true);
    try {
      const { url } = await uploadFile(file, 'cards/images');
      editor.chain().focus().setImage({ src: url }).run();
    } catch (err) {
      console.error('Upload failed:', err);
      alert('Upload failed. Please try again.');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [1, 2, 3] },
      }),
      Underline,
      Link.configure({ openOnClick: false }),
      Image.configure({
        inline: false,
        allowBase64: false,
        HTMLAttributes: {
          class: 'rounded-lg max-w-full h-auto',
        },
      }),
      TextStyle,
      Color,
      TextAlign.configure({ types: ['heading', 'paragraph'] }),
    ],
    content: content || '',
    onUpdate: ({ editor }) => {
      onUpdate(editor.getHTML());
    },
  });

  useEffect(() => {
    if (editor) {
      editor.view.dom.style.color = textColor;
      editor.view.dom.style.caretColor = textColor;
    }
  }, [textColor, editor]);

  const setLink = useCallback(() => {
    if (!editor) return;
    const previousUrl = editor.getAttributes('link').href;
    const url = window.prompt('Enter URL:', previousUrl || 'https://');
    if (url === null) return;
    if (url === '') {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
      return;
    }
    editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
  }, [editor]);

  if (!editor) return null;

  return (
    <div className="rich-text-editor">
      <style>{`
        .rich-text-editor .ProseMirror {
          min-height: 80px;
          outline: none;
          padding: 8px;
          white-space: pre-wrap;
          word-wrap: break-word;
        }
        .rich-text-editor .ProseMirror p.is-editor-empty:first-child::before {
          content: attr(data-placeholder);
          float: left;
          color: rgba(255,255,255,0.4);
          font-style: italic;
          pointer-events: none;
          height: 0;
        }
        .rich-text-editor .ProseMirror h1 { font-size: 1.5em; font-weight: bold; margin: 0.4em 0; }
        .rich-text-editor .ProseMirror h2 { font-size: 1.25em; font-weight: bold; margin: 0.3em 0; }
        .rich-text-editor .ProseMirror h3 { font-size: 1.1em; font-weight: bold; margin: 0.2em 0; }
        .rich-text-editor .ProseMirror blockquote {
          border-left: 3px solid currentColor;
          padding-left: 12px;
          opacity: 0.8;
          margin: 0.4em 0;
        }
        .rich-text-editor .ProseMirror ul { list-style: disc; padding-left: 24px; margin: 0.4em 0; }
        .rich-text-editor .ProseMirror ol { list-style: decimal; padding-left: 24px; margin: 0.4em 0; }
        .rich-text-editor .ProseMirror a { text-decoration: underline; cursor: pointer; }
        .rich-text-editor .ProseMirror p { margin: 0.2em 0; }
        .rich-text-editor .ProseMirror img { max-width: 100%; height: auto; border-radius: 8px; margin: 8px 0; }
      `}</style>

      <div className="bg-black/10 rounded-lg p-1.5 md:p-1 mb-2 flex flex-wrap gap-1 md:gap-0.5" style={{ color: textColor }}>
        {/* Text style */}
        <ToolbarButton onClick={() => editor.chain().focus().toggleBold().run()} isActive={editor.isActive('bold')} title="Bold">
          <b>B</b>
        </ToolbarButton>
        <ToolbarButton onClick={() => editor.chain().focus().toggleItalic().run()} isActive={editor.isActive('italic')} title="Italic">
          <i>I</i>
        </ToolbarButton>
        <ToolbarButton onClick={() => editor.chain().focus().toggleUnderline().run()} isActive={editor.isActive('underline')} title="Underline">
          <u>U</u>
        </ToolbarButton>
        <ToolbarButton onClick={() => editor.chain().focus().toggleStrike().run()} isActive={editor.isActive('strike')} title="Strikethrough">
          <s>S</s>
        </ToolbarButton>

        <div className="w-px bg-white/20 mx-0.5 self-stretch" />

        {/* Headings */}
        <ToolbarButton onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()} isActive={editor.isActive('heading', { level: 1 })} title="Heading 1">
          H1
        </ToolbarButton>
        <ToolbarButton onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} isActive={editor.isActive('heading', { level: 2 })} title="Heading 2">
          H2
        </ToolbarButton>
        <ToolbarButton onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()} isActive={editor.isActive('heading', { level: 3 })} title="Heading 3">
          H3
        </ToolbarButton>

        <div className="w-px bg-white/20 mx-0.5 self-stretch" />

        {/* Block */}
        <ToolbarButton onClick={() => editor.chain().focus().toggleBlockquote().run()} isActive={editor.isActive('blockquote')} title="Blockquote">
          &ldquo;
        </ToolbarButton>
        <ToolbarButton onClick={() => editor.chain().focus().toggleBulletList().run()} isActive={editor.isActive('bulletList')} title="Bullet List">
          &bull;
        </ToolbarButton>
        <ToolbarButton onClick={() => editor.chain().focus().toggleOrderedList().run()} isActive={editor.isActive('orderedList')} title="Ordered List">
          1.
        </ToolbarButton>

        <div className="w-px bg-white/20 mx-0.5 self-stretch" />

        {/* Alignment */}
        <ToolbarButton onClick={() => editor.chain().focus().setTextAlign('left').run()} isActive={editor.isActive({ textAlign: 'left' })} title="Align Left">
          &#8676;
        </ToolbarButton>
        <ToolbarButton onClick={() => editor.chain().focus().setTextAlign('center').run()} isActive={editor.isActive({ textAlign: 'center' })} title="Align Center">
          &#8596;
        </ToolbarButton>
        <ToolbarButton onClick={() => editor.chain().focus().setTextAlign('right').run()} isActive={editor.isActive({ textAlign: 'right' })} title="Align Right">
          &#8677;
        </ToolbarButton>

        <div className="w-px bg-white/20 mx-0.5 self-stretch" />

        {/* Link */}
        <ToolbarButton onClick={setLink} isActive={editor.isActive('link')} title="Link">
          🔗
        </ToolbarButton>

        {/* Image */}
        <ToolbarButton
          onClick={() => !uploading && imageInputRef.current?.click()}
          isActive={false}
          title="Insert Image"
        >
          {uploading ? '⏳' : '📷'}
        </ToolbarButton>
        <input
          type="file"
          ref={imageInputRef}
          accept="image/*"
          className="hidden"
          onChange={handleImageUpload}
        />

        {/* Color */}
        <label className="px-1 py-0.5 rounded cursor-pointer hover:bg-white/15 flex items-center justify-center min-w-[36px] h-[36px] md:min-w-[28px] md:h-[28px] relative" title="Text Color">
          <span className="text-xs font-bold">A</span>
          <input
            type="color"
            className="absolute inset-0 opacity-0 cursor-pointer w-full h-full"
            onInput={(e) => editor.chain().focus().setColor(e.target.value).run()}
            defaultValue={textColor}
          />
        </label>
      </div>

      <EditorContent
        editor={editor}
        className="rounded px-2 py-1 min-h-[30px]"
      />
    </div>
  );
};

export default RichTextEditor;
